import {
  type ID,
  type DateS,
  type ActionMove,
  type ActionMoveCorrectness,
  type THandGroup,
  type TRelativePosition,
  type TBoard,
} from '@/types/basic'
import type { ActionCorrectnessStr } from '@gto/api/vue-query'

export interface GameType {
  name: string
  game_properties: { format: string; variant: string; default_players: number }
  type: string
}
export interface GameMode {
  depth: string
  gametype: GameType
  stacks?: string[]
}

export interface Statistic {
  total_ev_loss: number
  total_ev_loss_as_pot: number
  total_frequency_difference: number
  gto_score: number
  mistake_moves: number
  inaccuracy_moves: number
  best_moves: number
  correct_moves: number
  wrong_moves: number
  blunder_moves: number
  played_moves: number
}

export type Tag = {
  id: ID
  created_at: DateS
  name: 'string'
  color_hex: 'string'
}

export type StreetActionsWithCorrectness = {
  action_code: ActionMove
  correctness: ActionMoveCorrectness
}

// https://api.dev.gtowizard.com/gto-wizard-admin/hh_filters/handcolumn/
// https://brandfolder.com/workbench/extract-text-from-image

export type ActionsWithCorrectness = StreetActionsWithCorrectness[][]

export type Position =
  | 'UTG'
  | 'UTG+1'
  | 'UTG+2'
  | 'UTG1'
  | 'UTG2'
  | 'LJ'
  | 'HJ'
  | 'CO'
  | 'BTN'
  | 'SB'
  | 'BB'
  | 'STR'
  | 'IP'
  | 'OOP'

export type Street = 'preflop' | 'flop' | 'turn' | 'river'

export type HandSource =
  | 'FILE_UPLOAD'
  | 'TEXT_UPLOAD'
  | 'TABLE_WIZARD'
  | 'PRACTICE'
  | 'POKER_WIZARD'

export type ReportTableRow = {
  actions_with_correctness?: ActionsWithCorrectness
  hand_correctness: ActionCorrectnessStr
  hand_id: ID
  total_pot?: number | string
  avg_frequency_difference?: string
  player_nickname?: string
  file_original_name?: string
  big_blind?: string
  small_blind?: string
  game_table?: string
  game_rake?: string
  hero_hand?: string
  total_frequency_difference?: string
  avg_ev_loss_as_pot?: string
  avg_gto_score?: string
  player_winloss?: string
  avg_ev_loss?: string
  pot_type?: string
  solution_warning_status?: string
  player_position?: Position
  game_analysis?: string
  boards?: string[]
  color_hex?: string
  note?: string
  game_type?: string
  game_format?: string
  site?: string
  source?: HandSource
  played_at?: string
}

export type ReportTableRowKeys = keyof ReportTableRow

export type NewReportTableRow = {
  created_at: DateS
  id: ID
  is_active: boolean
  is_opened: boolean
  is_saved: boolean
  last_viewed: DateS
  name: string
  ordering_position: number
  tags: Tag[]
}
export const DISABLED_OLD_ANALYZER_REPORT_COLUMNS = [
  'actions_with_correctness_preflop',
  'actions_with_correctness_flop',
  'actions_with_correctness_turn',
  'actions_with_correctness_river',
] as const
export type DisabledColumnFieldName = (typeof DISABLED_OLD_ANALYZER_REPORT_COLUMNS)[number]

export type NewReportTableRowKeys = keyof NewReportTableRow
export type OnlyColumnFieldName = 'actions_with_correctness'
export type OnlyHandFieldName = 'action' | 'correct_moves'
export type AdditionalHandField = 'note' | 'color_hex'
export type HandFieldName = ReportTableRowKeys | OnlyHandFieldName
export type ColumnFieldName =
  | ReportTableRowKeys
  | OnlyColumnFieldName
  | AdditionalHandField
  | DisabledColumnFieldName

export type HandsStatsFieldName =
  | 'avg_best_moves'
  | 'avg_best_moves_on_flop'
  | 'avg_best_moves_on_postflop'
  | 'avg_best_moves_on_preflop'
  | 'avg_blunder_moves'
  | 'avg_blunder_moves_on_flop'
  | 'avg_blunder_moves_on_postflop'
  | 'avg_blunder_moves_on_preflop'
  | 'avg_correct_moves'
  | 'avg_correct_moves_on_flop'
  | 'avg_correct_moves_on_postflop'
  | 'avg_correct_moves_on_preflop'
  | 'avg_ev_loss'
  | 'avg_ev_loss_as_pot'
  | 'avg_ev_loss_as_pot_on_flop'
  | 'avg_ev_loss_as_pot_on_postflop'
  | 'avg_ev_loss_as_pot_on_preflop'
  | 'avg_ev_loss_on_flop'
  | 'avg_ev_loss_on_postflop'
  | 'avg_ev_loss_on_preflop'
  | 'avg_ev_loss_per_mistake'
  | 'avg_frequency_difference'
  | 'avg_frequency_difference_on_flop'
  | 'avg_frequency_difference_on_postflop'
  | 'avg_frequency_difference_on_preflop'
  | 'avg_gto_score'
  | 'avg_gto_score_on_flop'
  | 'avg_gto_score_on_postflop'
  | 'avg_gto_score_on_preflop'
  | 'avg_inaccuracy_moves'
  | 'avg_inaccuracy_moves_on_flop'
  | 'avg_inaccuracy_moves_on_postflop'
  | 'avg_inaccuracy_moves_on_preflop'
  | 'avg_mistake_moves'
  | 'avg_mistake_moves_on_flop'
  | 'avg_mistake_moves_on_postflop'
  | 'avg_mistake_moves_on_preflop'
  | 'avg_moves'
  | 'avg_moves_on_flop'
  | 'avg_moves_on_postflop'
  | 'avg_moves_on_preflop'
  | 'avg_total_pot'
  | 'avg_wrong_moves'
  | 'avg_wrong_moves_on_flop'
  | 'avg_wrong_moves_on_postflop'
  | 'avg_wrong_moves_on_preflop'
  | 'count_formats'
  | 'count_hands'
  | 'count_sites'
  | 'total_best_moves'
  | 'total_best_moves_on_flop'
  | 'total_best_moves_on_postflop'
  | 'total_best_moves_on_preflop'
  | 'total_blunder_moves'
  | 'total_blunder_moves_on_flop'
  | 'total_blunder_moves_on_postflop'
  | 'total_blunder_moves_on_preflop'
  | 'total_correct_moves'
  | 'total_correct_moves_on_flop'
  | 'total_correct_moves_on_postflop'
  | 'total_correct_moves_on_preflop'
  | 'total_ev_loss'
  | 'total_ev_loss_as_pot'
  | 'total_ev_loss_as_pot_on_flop'
  | 'total_ev_loss_as_pot_on_postflop'
  | 'total_ev_loss_as_pot_on_preflop'
  | 'total_ev_loss_on_flop'
  | 'total_ev_loss_on_postflop'
  | 'total_ev_loss_on_preflop'
  | 'total_frequency_difference'
  | 'total_frequency_difference_on_flop'
  | 'total_frequency_difference_on_postflop'
  | 'total_frequency_difference_on_preflop'
  | 'total_gto_score'
  | 'total_gto_score_on_flop'
  | 'total_gto_score_on_postflop'
  | 'total_gto_score_on_preflop'
  | 'total_inaccuracy_moves'
  | 'total_inaccuracy_moves_on_flop'
  | 'total_inaccuracy_moves_on_postflop'
  | 'total_inaccuracy_moves_on_preflop'
  | 'total_mistake_moves'
  | 'total_mistake_moves_on_flop'
  | 'total_mistake_moves_on_postflop'
  | 'total_mistake_moves_on_preflop'
  | 'total_moves'
  | 'total_moves_on_flop'
  | 'total_moves_on_postflop'
  | 'total_moves_on_preflop'
  | 'total_winloss'
  | 'total_wrong_moves'
  | 'total_wrong_moves_on_flop'
  | 'total_wrong_moves_on_postflop'
  | 'total_wrong_moves_on_preflop'

export type ReportFieldName =
  | 'id'
  | 'name'
  | 'last_viewed'
  | 'ordering_position'
  | 'tags'
  | 'selected'
  | 'details'

export type RangeValue = (number | string)[]

export type TUiTypeValues = {
  actions_with_correctness: ActionsWithCorrectness
  action_with_correctness: StreetActionsWithCorrectness[]
  action_sequence: string
  hand_correctness: string
  aggressor_defender: string
  big_blind: string | number
  big_blinds: RangeValue
  board: TBoard
  boards: TBoard[]
  boolean_icon: 'null' | 'true' | 'false'
  color_dot: string
  date: string
  decimal_two: number
  decimal_three: number
  game_format: string
  game_table: string
  gtow_score: number
  hand: TBoard
  hand_group: THandGroup
  percentage_decimal_one: number
  player_position: Position
  opponent_position: Position
  pot_type: string
  range_big_blind: RangeValue
  range_decimal_two: RangeValue
  range_percentage_decimal_one: RangeValue
  readonly_filename: string
  relative_position: TRelativePosition
  site: string
  source: HandSource
  status: string
  street: string
  text: string
}

export type TUiType = keyof TUiTypeValues

export type UiColor = {
  severity: 'severity1' | 'severity2' | 'severity3'
  from: number
  to: number
}
export interface HandHistoryColumn {
  field_name: ColumnFieldName
  id: ID
  is_ordering_available: boolean
  totals_field_name: HandsStatsFieldName
  ui_value_type: TUiType
  ui_colors: UiColor[]
}

export interface HandHistoryReportColumn {
  column: HandHistoryColumn
  id: ID
  is_hidden: boolean
  ordering_position: number
  sort_direction: 'ASC' | 'DESC' | null
  is_disabled?: true
}

export type AnalyzerReport = {
  id: ID
  tags: Tag[]
  is_active: boolean
  is_saved: boolean
  is_opened: boolean
  ordering_position: number
  created_at: DateS
  name: string
  selected_view: 'TABLE' | 'STATS'
}

export type FilterValue = string | number | (string | number)[] | null
export type ApiFilterType = 'STRING' | 'KEY' | 'NUMERIC' | 'BOOLEAN'
export type FilterType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'RANGE' | 'SEARCH_TEXT'

export type ApiFilter = {
  field_name: string
  id: ID
  predefined_value: FilterValue
  type: ApiFilterType
  filter_type: FilterType
}

export type FilterUIType =
  | 'SELECT'
  | 'RANGE_SELECT'
  | 'INPUT'
  | 'INPUT_AND_SELECT'
  | 'SELECT_WITH_SEARCH'
  | 'HAND'
  | 'DATETIME'
  | 'COLOR_TAG'
  | 'ACTIONS_SEQUENCE'

export interface TFilterField {
  api_filters: ApiFilter[]
  available_choices?: string[]
  available_type_choices?: string[]
  display_key?: string
  field_name: string
  filter_key?: string
  filter_ui_type: FilterUIType
  id: ID
  range_keys?: string[]
  search_field_url?: string
  ui_value_type: TUiType
}

export interface FilterMetadata {
  display_value: FilterValue
}

export interface Filter {
  filter: TFilterField
  id: ID
  is_active: boolean
  ordering_position: number
  value: FilterValue // also ActionSequence for actions_sequence (skipped for now)
  metadata: FilterMetadata // for UI - FE add this when creating a report with file_id
  is_disabled?: true
}

export type ActiveAnalyzerReport = AnalyzerReport & {
  saved_filters: Filter[]
  saved_columns: HandHistoryReportColumn[]
}

export type ReportParams = {
  is_saved?: boolean
  is_opened?: boolean
  search?: string
  entities?: ID[]
}

export type FilterCategory = { category_name: string; filters: TFilterField[] }
